import { useState } from "react";
import {
  Modal,
  Box,
} from "@mui/material";
import AutocompleteComponent from "@components/AutocompleteComponent/AutocompleteComponent";
import ButtonComponent from "@components/ButtonComponent";
import TableComponent from "@components/TableComponent/TableComponent";
import SheetErrorModal from "./SheetErrorModal";
import { sheetCreateAdSet } from "@services/sheetCreateAdSet";
import { Loader } from "@components/Loader";

const SheetAdSetCreationModal = ({
  adsetsToCreate,
  freshAdsetsData,
  existingAdsets,
  campaignsMap,
  open,
  onClose,
  loadingAdsets = false,
  setShowSummaryModal,
  setAdCountsSummaryData,
  setExistingAdsets,
  adCountsSummary,
  userId,
  adAccountId
}) => {
  const [selectedAdSet, setSelectedAdSet] = useState(null);
  const [isTypeSwitchOn, setIsTypeSwitchOn] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorData, setErrorData] = useState({ type: '', message: '', details: '' });

  // Handler for Create Ad Set button
  const handleCreateAdSet = async () => {
    if (!selectedAdSet) return;
    setIsCreating(true);
    try {
      const payload = {
        user_id: userId,
        ad_account_id: adAccountId,
        reference_adset_id: selectedAdSet.adsetId,
        adsets_to_create: adsetsToCreate,
        campaigns_map: campaignsMap,
        existing_adsets: existingAdsets,
        adset_status: isTypeSwitchOn ? 'ACTIVE' : 'PAUSED'
      };
      const response = await sheetCreateAdSet(payload);
      // If response is negative, show error modal
      if (!response || response.error || response.success === false) {
        setErrorData({
          type: 'API Error',
          message: response?.message || 'Failed to create ad set.',
          details: response?.error || response?.details || ''
        });
        setShowErrorModal(true);
      } else {
        // Success: close modal and show summary
        if (typeof onClose === "function") onClose();
        // Send new existing_adsets to summary modal
        if (response) {
          setExistingAdsets(response[1].existing_adsets);
        }
        if (typeof setShowSummaryModal === "function") {
          setAdCountsSummaryData(adCountsSummary);
          setShowSummaryModal(true);
        }
      }
    } catch (error) {
      setErrorData({
        type: 'API Error',
        message: error?.message || 'Failed to create ad set.',
        details: error?.response?.data?.error || error?.toString() || ''
      });
      setShowErrorModal(true);
      console.error('Error creating ad set:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleTypeSwitchChange = (event) => {
    setIsTypeSwitchOn(event.target.checked);
  };

  const resetModalState = () => {
    setSelectedAdSet(null);
  };

  const handleClose = () => {
    resetModalState();
    if (typeof onClose === "function") {
      onClose();
    }
  };

  const handleAdSetSelection = (event, newValue) => {
    setSelectedAdSet(newValue);
  };

  // Table columns
  const columns = [
    {
      key: 'campaignName', label: 'Campaign Name', width: '100px',
      cellStyle: {
        wordWrap: 'break-word',
        wordBreak: 'break-word',
        whiteSpace: 'normal',
        overflow: 'hidden',
        textAlign: 'center'
      }
    },
    {
      key: 'adsetName',
      label: 'Adset Name',
      width: '300px',
      cellStyle: {
        wordWrap: 'break-word',
        wordBreak: 'break-word',
        whiteSpace: 'normal',
        overflow: 'hidden'
      }
    },
    {
      key: 'dailyBudget',
      label: 'Daily Budget',
      width: '70px',
      tdClass: 'text-center',
      cellStyle: {
        wordWrap: 'break-word',
        wordBreak: 'break-word',
        whiteSpace: 'normal',
        overflow: 'hidden',
        textAlign: 'center'
      }
    },
  ];
  // Build table data from adsetsToCreate
  const data = [];
  if (adsetsToCreate && typeof adsetsToCreate === 'object') {
    Object.entries(adsetsToCreate).forEach(([campaignName, adsets]) => {
      if (Array.isArray(adsets)) {
        adsets.forEach((adset) => {
          data.push({
            campaignName: campaignName,
            adsetName: adset.name,
            dailyBudget: adset.daily_budget,
          });
        });
      }
    });
  }

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="sheet-adset-selection-modal-title"
        aria-describedby="sheet-adset-selection-modal-description"
      >
        <Box
          className="absolute top-1/2 left-1/2 flex flex-col gap-6 bg-white rounded-2xl border border-gray-300 p-6 shadow-[0px_10px_10px_-5px_rgba(0,0,0,0.04),0px_20px_25px_-5px_rgba(0,0,0,0.10)]"
          style={{ transform: 'translate(-50%, -50%)', width: 1000 }}
        >
          {/* Loader overlay while creating adset */}
          {isCreating && (
            <div className="absolute inset-0 z-50 flex items-center justify-center bg-white bg-opacity-70">
              <Loader />
            </div>
          )}
          {/* Header */}
          <Box className="flex items-center justify-between">
            <h2
              id="sheet-adset-selection-modal-title"
              className="font-inter font-medium text-[22px] leading-[120%] m-0 text-[#1F1F1F]"
            >
              Select a reference ad set
            </h2>
            <ButtonComponent
              onClick={handleClose}
              className="absolute text-gray-400 transition-colors right-5 top-5 hover:text-gray-600"
              variant="ghost"
              aria-label="Close"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </ButtonComponent>
          </Box>

          {/* Content */}
          <div className="flex flex-col gap-6">
            {/* Reference Adset */}
            <AutocompleteComponent
              options={freshAdsetsData}
              value={selectedAdSet}
              onChange={handleAdSetSelection}
              loading={loadingAdsets}
              label="Reference Ad Set"
              showLabel={true}
              placeholder="Select Reference Ad Set"
              primaryField="name"
              secondaryField="campaignName"
              getOptionLabel={(option) => {
                if (!option) return "";
                // Combine adset name and campaign name for search
                return [option.name, option.campaignName].filter(Boolean).join(" - ");
              }}
              filterOptions={(options, { inputValue }) => {
                // Custom filter: match input against name or campaignName
                return options.filter(opt => {
                  const name = opt.name?.toLowerCase() || "";
                  const campaign = opt.campaignName?.toLowerCase() || "";
                  const input = inputValue.toLowerCase();
                  return name.includes(input) || campaign.includes(input);
                });
              }}
              isOptionEqualToValue={(option, value) => {
                // Use a unique identifier for adset comparison
                if (!option || !value) return false;
                if (option.id && value.id) return option.id === value.id;
                if (option.adsetId && value.adsetId) return option.adsetId === value.adsetId;
                // Fallback to name and campaignName if no id/adsetId
                return option.name === value.name && option.campaignName === value.campaignName;
              }}
              disabled={loadingAdsets}
              width={'100%'}
              height={46}
              startIcon="target.svg"
              startIconAlt="Target"
            />
          </div>

          <div>
            <TableComponent
              columns={columns}
              data={data}
              headerCellStyle={{ fontWeight: 'font-normal', fontSize: 'text-[15px]', fontColor: 'text-[#6B7280]' }}
              styleProps={{ maxBodyHeight: 400 }}
            />
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between gap-4">
            <ButtonComponent
              isSwitch
              switchProps={{
                checked: isTypeSwitchOn,
                onChange: handleTypeSwitchChange,
                inputProps: { 'aria-label': 'Type Switch' },
                color: 'primary',
              }}
              switchLabel="Ad Set Active"
              className="font-inter text-sm text-[#1F1F1F]"
              style={{ minWidth: 120 }}
            />
            <ButtonComponent
              onClick={handleCreateAdSet}
              variant="primary"
              style={{ minWidth: 120 }}
              disabled={!selectedAdSet || isCreating}
            >
              {isCreating ? "Creating..." : "Create Ad Set"}
            </ButtonComponent>
          </div>
        </Box>
      </Modal>
      <SheetErrorModal open={showErrorModal} onClose={() => setShowErrorModal(false)} error={errorData} />
    </>
  );
};

export default SheetAdSetCreationModal;